import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTranslation } from "../../i18n/useTranslation";
import { useAuth } from "../../AuthContext";
import { toBcp47Locale } from "./wizardLocale";

function formatSchedule(startTime, endTime, notScheduledText, locale) {
  if (!startTime || !endTime) return notScheduledText;
  try {
    const s = new Date(startTime);
    const e = new Date(endTime);
    const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
    return `${s.toLocaleDateString(locale, opts)} \u2014 ${e.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}`;
  } catch {
    return notScheduledText;
  }
}

export default function WizardStep4Publish({
  isDarkMode,
  testDetails = {},
  questionCount = 0,
  totalPoints = 0,
  studentCount = 0,
  visibility = "private",
  sharedClassCount = 0,
  onBack,
  onPublish,
  publishing = false,
  publishError = "",
  onDismissError,
}) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();
  const { selectedSecondaryLang } = useAuth();
  const locale = toBcp47Locale(selectedSecondaryLang);

  const visibilityLabel =
    visibility === "public"
      ? t("Public")
      : visibility === "organization"
      ? t("Organization")
      : t("Private");

  return (
    <View>
      <View style={[styles.summary, { backgroundColor: c.cardBg, borderColor: c.border }]}>
        {/* Icon */}
        <View style={[styles.pubIcon, { backgroundColor: c.accentLight }]}>
          <MaterialCommunityIcons
            name="check-circle-outline"
            size={36}
            color={c.accent}
          />
        </View>

        {/* Heading */}
        <Text style={[styles.pubHeading, { color: c.textPrimary }]}>{t("Ready to Publish")}</Text>
        <Text style={[styles.pubDesc, { color: c.textSecondary }]}>
          {t("Your test is configured and ready to go live. Students will be able to access it during the scheduled window.")}
        </Text>

        {/* Test info card */}
        <View style={[styles.infoCard, { backgroundColor: c.sidebarBg }]}>
          <Text style={[styles.infoTitle, { color: c.textPrimary }]}>
            {testDetails.title || t("Untitled Test")}
          </Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Text style={[styles.infoLabel, { color: c.textSecondary }]}>{t("Schedule:")} </Text>
              <Text style={[styles.infoValue, { color: c.textPrimary }]}>
                {formatSchedule(testDetails.start_time, testDetails.end_time, t("Not scheduled"), locale)}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={[styles.infoLabel, { color: c.textSecondary }]}>{t("Duration:")} </Text>
              <Text style={[styles.infoValue, { color: c.textPrimary }]}>
                {testDetails.duration_minutes || t("N/A")} {t("minutes")}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={[styles.infoLabel, { color: c.textSecondary }]}>{t("Visibility:")} </Text>
              <Text style={[styles.infoValue, { color: c.textPrimary }]}>{visibilityLabel}</Text>
            </View>
            <View style={styles.infoItem}>
              <Text style={[styles.infoLabel, { color: c.textSecondary }]}>{t("Shared with:")} </Text>
              <Text style={[styles.infoValue, { color: c.textPrimary }]}>
                {sharedClassCount === 1
                  ? t("{count} class", { count: sharedClassCount })
                  : t("{count} classes", { count: sharedClassCount })}
              </Text>
            </View>
          </View>
        </View>

        {/* Stats */}
        <View style={styles.pubStats}>
          <View style={styles.pubStat}>
            <Text style={[styles.pubStatVal, { color: c.accent }]}>{questionCount}</Text>
            <Text style={[styles.pubStatLabel, { color: c.textMuted }]}>{t("Questions")}</Text>
          </View>
          <View style={styles.pubStat}>
            <Text style={[styles.pubStatVal, { color: c.accent }]}>{totalPoints}</Text>
            <Text style={[styles.pubStatLabel, { color: c.textMuted }]}>{t("Total Points")}</Text>
          </View>
          <View style={styles.pubStat}>
            <Text style={[styles.pubStatVal, { color: c.accent }]}>~{studentCount}</Text>
            <Text style={[styles.pubStatLabel, { color: c.textMuted }]}>{t("Students")}</Text>
          </View>
        </View>

        {/* Inline error banner — surfaces server / validation errors that the
            React Native Web Alert.alert polyfill does not reliably render. */}
        {publishError ? (
          <View
            style={[
              styles.errorBanner,
              {
                backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.12)" : "#fef2f2",
                borderColor: isDarkMode ? "rgba(248, 113, 113, 0.4)" : "#fecaca",
              },
            ]}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
          >
            <MaterialCommunityIcons
              name="alert-circle-outline"
              size={20}
              color={isDarkMode ? "#fca5a5" : "#b91c1c"}
              style={{ marginTop: 1 }}
            />
            <Text
              style={[
                styles.errorBannerText,
                { color: isDarkMode ? "#fecaca" : "#991b1b" },
              ]}
            >
              {publishError}
            </Text>
            {onDismissError ? (
              <TouchableOpacity
                onPress={onDismissError}
                accessibilityLabel={t("Dismiss error")}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <MaterialCommunityIcons
                  name="close"
                  size={18}
                  color={isDarkMode ? "#fca5a5" : "#b91c1c"}
                />
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        {/* Action buttons */}
        <View style={styles.pubActions}>
          <TouchableOpacity
            style={[styles.btnSecondary, { borderColor: c.border, backgroundColor: c.cardBg }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="chevron-left" size={16} color={c.textPrimary} />
            <Text style={[styles.btnSecondaryText, { color: c.textPrimary }]}>{t("Back to Edit")}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.publishBtn, { backgroundColor: c.accent }]}
            onPress={onPublish}
            activeOpacity={0.7}
            disabled={publishing}
          >
            {publishing ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <>
                <MaterialCommunityIcons name="check" size={16} color="#fff" />
                <Text style={styles.publishBtnText}>{t("Publish Test")}</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      cardBg: "#1e293b",
      sidebarBg: "#162032",
      accent: "#818cf8",
      accentLight: "rgba(99,102,241,0.12)",
    };
  }
  return {
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    cardBg: "#ffffff",
    sidebarBg: "#f1f5f9",
    accent: "#6366f1",
    accentLight: "#eef2ff",
  };
}

const styles = StyleSheet.create({
  summary: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 32,
    maxWidth: 640,
    alignSelf: "center",
    alignItems: "center",
    width: "100%",
  },
  pubIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  pubHeading: {
    fontSize: 24,
    fontWeight: "400",
    marginBottom: 8,
    textAlign: "center",
  },
  pubDesc: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 21,
    maxWidth: 480,
  },

  /* Info card */
  infoCard: {
    width: "100%",
    borderRadius: 10,
    padding: 18,
    marginBottom: 24,
  },
  infoTitle: {
    fontWeight: "700",
    fontSize: 16,
    marginBottom: 12,
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  infoItem: {
    width: "48%",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  infoLabel: { fontSize: 13 },
  infoValue: { fontSize: 13, fontWeight: "700" },

  /* Stats */
  pubStats: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 28,
    width: "100%",
    justifyContent: "center",
  },
  pubStat: {
    flex: 1,
    alignItems: "center",
  },
  pubStatVal: { fontSize: 28, fontWeight: "800" },
  pubStatLabel: { fontSize: 12, marginTop: 2 },

  /* Error banner */
  errorBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    width: "100%",
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    marginBottom: 16,
  },
  errorBannerText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "500",
  },

  /* Actions */
  pubActions: {
    flexDirection: "row",
    gap: 12,
    justifyContent: "center",
  },
  btnSecondary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 10,
    borderWidth: 1,
  },
  btnSecondaryText: { fontSize: 14, fontWeight: "600" },
  publishBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 10,
    shadowColor: "rgba(99,102,241,0.25)",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  publishBtnText: { color: "#fff", fontSize: 15, fontWeight: "600" },
});
